<script lang="ts">
  import Prose from '../lib/components/Prose.svelte';
</script>

<section id="rubiks-cube" class="section">
  <Prose>
    <header class="header">
      <div class="eyebrow">Case study</div>
      <h2>The Rubik's Cube exposes the edge</h2>
    </header>

    <p>
      The cube was the first place the boundary became obvious. After several
      prompt iterations, Opus could solve 1-, 2-, and 3-step scrambles. At four
      steps, performance fell off sharply, and no prompt I tried moved the edge.
    </p>

    <p>
      The model was trained across a huge range of text tasks. Solving a
      Rubik's Cube is not really one of them. The weights contain the vocabulary
      for describing cube moves, but not a reliable representation of the spatial
      transformations themselves. A child can learn the algorithms over a
      weekend; a Python library can solve the cube immediately. That was not the
      point. The point was whether prompting could make the model execute the
      procedure.
    </p>

    <p>
      Procedural scaffolds help only when they bottom out in reliable steps. For
      multiplication, the model can decompose a large problem into single-digit
      facts that are densely memorized. The scaffold lowers the task onto facts
      the model can recall with high fidelity.
    </p>

    <p>
      The cube can be decomposed too: phase, piece, algorithm, move. But the
      leaves are different. The decomposition eventually asks for a state update
      the weights do not reliably perform: what the cube looks like after one
      move. Once that leaf is unreliable, the model stalls or loops instead of
      converging.
    </p>

    <p>
      The cube fails for the same reason the arithmetic tasks fail:
      P(success) = p<sub>step</sub><sup>N</sup>. Here, p<sub>step</sub> is low
      because spatial transformations appear to be much less densely represented
      than single-digit arithmetic. N is long because every move depends on the
      previous state. Verification uses the same weights and misses the same
      state errors. Once the model is past the edge, it has no reliable signal
      that the attempt is no longer converging.
    </p>
  </Prose>
</section>

<style>
  .section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--space-4xl);
    width: 100%;
    min-width: 0;
  }
  .header {
    margin-bottom: var(--space-lg);
  }
  .eyebrow {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: var(--space-sm);
  }
  .summary {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: var(--text-lg);
    color: var(--ink-muted);
    line-height: var(--leading-snug);
    margin-top: var(--space-sm);
    margin-bottom: var(--space-lg);
  }
</style>
